import { type Options } from 'highcharts';
import clamp from 'lodash/clamp';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import { useFlareContext } from '@/components/Flare/FlareContext';
import { type AxisExtended, type FlareChart } from '@/components/Flare/types';
import { useNotesDrawerContext } from '@/components/panels';
import { cn } from '@/lib/utils';

const buildNotesOptions = (
  options: Options,
  onChartRedraw: (chart: FlareChart) => void,
): Options => {
  return {
    ...options,
    chart: {
      ...options.chart,
      events: {
        ...options.chart?.events,
        redraw: function (event) {
          options.chart?.events?.redraw?.call(this, event);
          onChartRedraw(this as FlareChart);
        },
      },
    },
  };
};

const Notes = () => {
  const { id, registerChild, parentProps } = useFlareContext();
  const { eventLog, selectedNote, onSelectedNoteChange, onNotesDrawerOpenChange } =
    useNotesDrawerContext();
  const [xAxis, setXAxis] = useState<AxisExtended | undefined>();
  const [plotBounds, setPlotBounds] = useState<{
    plotTop: number;
    plotLeft: number;
    plotHeight: number;
    plotWidth: number;
  }>();
  const [container, setContainer] = useState<Element | null>(null);

  const handleRender = useCallback((chart: FlareChart) => {
    if (chart) {
      setXAxis(chart.xAxis[0]);
      setPlotBounds({
        plotTop: chart.plotTop,
        plotLeft: chart.plotLeft,
        plotHeight: chart.plotHeight,
        plotWidth: chart.plotWidth,
      });
      setContainer(chart.container?.parentNode as Element);
    }
  }, []);

  const sortedEventLog = useMemo(() => eventLog && [...eventLog].reverse(), [eventLog]);

  useEffect(() => {
    registerChild(id, (options: Options) => buildNotesOptions(options, handleRender));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventLog, handleRender]);

  if (!container || !eventLog || !plotBounds || !xAxis) {
    return null;
  }

  const { parseX } = parentProps;
  const maxXPos = xAxis.toPixels(xAxis.dataMax, true);
  const minXPos = xAxis.toPixels(xAxis.dataMin, true);

  return createPortal(
    <div className="z-1">
      {sortedEventLog?.map((note) => {
        const date = parseX ? parseX(note.date) : note.date;

        // If the note happens in the future or past, snap it to the nearest edge of the chart.
        const xPos = clamp(xAxis.toPixels(date, true), minXPos, maxXPos);
        const isSelected = note.id === selectedNote?.id;

        return (
          <div
            key={note.id}
            className={cn(
              'group absolute -translate-x-1/2 pointer-events-none hover:z-1',
              isSelected && 'z-1',
            )}
            data-id={note.id}
            data-xpos={isNaN(xPos) ? 0 : xPos}
            style={{
              left: `${Math.round(xPos + plotBounds.plotLeft)}px`,
              top: `${plotBounds?.plotTop}px`,
              height: `${plotBounds?.plotHeight}px`,
            }}
            onMouseEnter={() => onSelectedNoteChange?.(note)}
            onMouseLeave={() => onSelectedNoteChange?.(undefined)}
            onClick={() => {
              onNotesDrawerOpenChange?.(true);
              onSelectedNoteChange?.(note);
            }}
          >
            <div
              className={cn(
                'absolute inset-y-0 left-1/2 -translate-x-1/2 border-l border-dashed border-green-400',
                'invisible group-hover:visible',
                isSelected && 'visible',
              )}
            />
            <div
              className={cn(
                'pointer-events-auto relative top-full size-3.5 -translate-y-1/2 cursor-pointer bg-contain bg-center bg-no-repeat',
                "bg-[url('/assets/svg/bx-message-square-detail.svg')]",
                'group-hover:bg-[url("/assets/svg/bx-message-square-detail-hover.svg")]',
                isSelected && 'bg-[url("/assets/svg/bx-message-square-detail-hover.svg")]',
              )}
            />
          </div>
        );
      })}
    </div>,
    container,
  );
};

export default Notes;
